import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';

import { CustomEventService } from '@shared/services/custom-event.service';
import { DashboardService } from '@shared/services/dashboard.service';
import { GlobalSettingsService } from '@shared/services/global-settings.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import DateUtil from '@shared/utils/date.util';
import { downloadImage } from '@shared/utils/download-widget.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-widget-header',
  templateUrl: './widget-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetHeaderComponent implements OnInit {

  private _loading: boolean;
  private _selectedDate: string;

  readableSelectedDate: string;
  constants: any;
  datePeriods = new Map();

  dateDropDownShown: boolean;
  noteDropDownShown: boolean;
  dateFormat: string;

  editMode = false;

  @Input() widgetName: string;
  @Input() headerComponents: string;
  @Input() headerAddon: string;
  @Input() toolTipHTML: string;
  @Input() widgetId: string;
  @Input() dateFilterable: boolean;
  @Input() note?: string;
  @Input() url?: string;
  @Input() showDownloadIcon? = true;
  @Input()
  set loading(loading: boolean) {
    this._loading = loading;
    this.changeDetectorRef.detectChanges();
  }

  get loading(): boolean {
    return this._loading;
  }

  @Input()
  set selectedDate(selectedDate: string) {
    this._selectedDate = selectedDate;
    this.readableSelectedDate = (selectedDate && this.getReadableDate(selectedDate)) || null;
    this.changeDetectorRef.detectChanges();
  }

  get selectedDate(): string {
    // returns number for datepicker dropdown when necessary
    return this._selectedDate;
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private customEventService: CustomEventService,
    private dashboardService: DashboardService,
    private globalSettingsService: GlobalSettingsService,
  ) {
    this.dateFormat = this.globalSettingsService.dateFormat;

    const constants = this.dashboardService.dashboardConstants.get('constants');

    this.constants = constants || {};

    if (this.constants.datePeriods) {
      for (const period of this.constants.datePeriods) {
        this.datePeriods.set(period.key, period.label);
      }
    }
  }

  ngOnInit(): void {
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(
      ({ action, data }) => {
        if (action === ActionTypesEnum.CLOSE_DROPDOWN) {
          this.dateDropDownShown = false;
        }

        if (action === ActionTypesEnum.EDIT_DASHBOARD) {
          this.editMode = data;
        }
      });
  }


  getReadableDate(date): string {
    if (!date) {
      return '';
    }

    if (this.datePeriods.has(date)) {
      return this.datePeriods.get(date);
    } else if (typeof +date === 'number') {
      return `Since ${DateUtil.formatDate(+date, this.dateFormat)}`;
    }
  }

  addNote(): void {
    const data = {
      id: this.widgetId,
      note: this.note,
      url: this.url
    };

    this.customEventService.announceEvent(ActionTypesEnum.UPDATE_WIDGET_NOTE, data);
    this.noteDropDownShown = false;
  }

  downloadWidget(): void {
    if (this.editMode) {
      return;
    }
    downloadImage(this.widgetId, this.widgetName);
    this.customEventService.announceEvent(ActionTypesEnum.UPDATE_SCREENSHOT, true);
    setTimeout((): void => {
      this.customEventService.announceEvent(ActionTypesEnum.UPDATE_SCREENSHOT, false);
    }, 100);
  }

  duplicateWidget(): void {
    this.customEventService.announceEvent(ActionTypesEnum.DUPLICATE_WIDGET, this.widgetId);
  }
}
